import { format, isThisWeek, isToday, isYesterday, parse, parseISO } from 'date-fns';
import { enUS as en, es, pt } from 'date-fns/locale';

import { useUserDataStore } from '../newStore/userDataStore';
import {
  DateFormatProfileType,
  DEFAULT_DATE_FORMAT_PROFILE,
  getDateFormatConfig,
} from '../types/dateFormat';


export const dateLocales = { es, pt, en };

/**
 * Default formats used for parsing dates from backend
 * Order matters: most specific formats should come first
 */
const DEFAULT_PARSE_FORMATS = [
  'dd-MM-yyyy HH:mm',
  'dd/MM/yyyy HH:mm',
  'dd-MM-yyyy',
  'dd/MM/yyyy',
  'yyyy-MM-dd',
  'HH:mm',
] as const;

/**
 * Parse date string from backend with multiple format support
 * Centralizes date parsing logic to avoid code duplication across components
 *
 * @param value - Date string to parse
 * @param formats - Optional array of formats to try (in order of priority)
 * @returns Date object or null if all formats fail
 */
export const parseDateString = (
  value: string,
  formats: readonly string[] = DEFAULT_PARSE_FORMATS,
): Date | null => {
  if (!value) return null;

  const trimmedValue = value.trim();

  // Try parsing with each format
  for (const fmt of formats) {
    try {
      const parsed = parse(trimmedValue, fmt, new Date());
      if (!isNaN(parsed.valueOf())) return parsed;
    } catch {
      continue;
    }
  }

  // Fallback: try ISO format (handles strings like "2024-12-25T10:30:00Z")
  try {
    const iso = new Date(trimmedValue);
    if (!isNaN(iso.getTime())) return iso;
  } catch {
    // Ignore
  }

  return null;
};

/**
 * Detects the date type based on the string format
 * Used when we need to know if a value is date, datetime, or time
 *
 * @param value - Date string to analyze
 * @returns 'datetime' | 'date' | 'time' | null
 */
export const detectDateType = (value: string): 'datetime' | 'date' | 'time' | null => {
  if (!value) return null;

  const trimmedValue = value.trim();

  // Check datetime patterns (most specific first)
  const datetimeFormats = ['dd-MM-yyyy HH:mm', 'dd/MM/yyyy HH:mm'];
  for (const fmt of datetimeFormats) {
    try {
      const parsed = parse(trimmedValue, fmt, new Date());
      if (!isNaN(parsed.valueOf())) return 'datetime';
    } catch {
      continue;
    }
  }

  // Check date patterns
  const dateFormats = ['dd-MM-yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'];
  for (const fmt of dateFormats) {
    try {
      const parsed = parse(trimmedValue, fmt, new Date());
      if (!isNaN(parsed.valueOf())) return 'date';
    } catch {
      continue;
    }
  }

  // Check time pattern
  try {
    const parsed = parse(trimmedValue, 'HH:mm', new Date());
    if (!isNaN(parsed.valueOf())) return 'time';
  } catch {
    // Ignore
  }

  return null;
};

/**
 * Standard example date used for placeholders and format previews
 * November 4, 1999 14:30
 */
export const EXAMPLE_DATE = new Date(1999, 10, 4, 14, 30);

/**
 * Formats the example date according to user's format preference
 * Used for generating placeholders and format previews
 *
 * @param formatType - Type of format to use (date, time, datetime)
 * @param lang - Language locale for formatting
 * @param profile - Optional date format profile
 * @returns Formatted example date string
 */
export const formatExampleDate = (
  formatType: 'date' | 'time' | 'datetime' = 'date',
  lang: keyof typeof dateLocales = 'en',
  profile?: DateFormatProfileType,
): string => {
  const localeLanguage = dateLocales[lang];
  const formatConfig = getDateFormatConfig(profile ?? null);

  try {
    return format(EXAMPLE_DATE, formatConfig[formatType], { locale: localeLanguage });
  } catch {
    return formatConfig[formatType];
  }
};

export const relativeDateLocales = {
  es: {
    today: 'Hoy',
    yesterday: 'Ayer',
  },
  pt: {
    today: 'Hoje',
    yesterday: 'Ontem',
  },
  en: {
    today: 'Today',
    yesterday: 'Yesterday',
  },
};

/**
 * Gets format options based on user's date format preference
 * Returns formats compatible with date-fns for use in DatePicker components
 * 
 * @param profile - Optional date format profile. If not provided, uses current user's preference
 * @returns Format configuration object with date, time, and datetime formats
 */
export const getFormatOptions = (profile?: DateFormatProfileType) => {
  // Get profile from parameter or current user preference
  const dateProfile: DateFormatProfileType =
    profile !== undefined
      ? profile
      : (useUserDataStore.getState().currentUser
          ?.date_format_preference as DateFormatProfileType) || null;

  const formatConfig = getDateFormatConfig(dateProfile);
  return formatConfig;
};

/**
 * Default formats (kept for backward compatibility)
 * @deprecated Use getFormatOptions() instead to respect user's date format preference
 * This uses the default profile (european) and may not match user preferences
 */
export const formatOptions = getFormatOptions(DEFAULT_DATE_FORMAT_PROFILE);

/**
 * Gets the date format to use according to the type and account preference
 */
const getDateFormatToUse = (
  formatType: 'date' | 'time' | 'datetime',
  profile: DateFormatProfileType,
): string => {
  const formatConfig = getDateFormatConfig(profile);
  return formatConfig[formatType];
};

export const formatDatascopeDate = (
  datetime: string | Date,
  formatType: 'date' | 'time' | 'datetime' = 'datetime',
  lang: keyof typeof dateLocales = 'en',
  absoluteFormat = false,
  accountProfile?: DateFormatProfileType,
): string => {
  const localeLanguage = dateLocales[lang];

  const date = typeof datetime === 'string' ? parseISO(datetime) : datetime;

  // Get account profile: use provided parameter or fallback to store
  const profile: DateFormatProfileType =
    accountProfile !== undefined
      ? accountProfile
      : (useUserDataStore.getState().currentUser
          ?.date_format_preference as DateFormatProfileType) || null;

  // Get format to use according to the profile
  const dateFormatToUse = getDateFormatToUse(formatType, profile);

  if (absoluteFormat || formatType === 'time') {
    return format(date, dateFormatToUse, { locale: localeLanguage });
  }

  if (isToday(date)) {
    if (formatType === 'date') return relativeDateLocales[lang].today;
    const formattedTime = format(date, getDateFormatToUse('time', profile), {
      locale: localeLanguage,
    });
    return `${relativeDateLocales[lang].today} ${formattedTime}`;
  }

  if (isYesterday(date)) {
    if (formatType === 'date') return relativeDateLocales[lang].yesterday;
    const formattedTime = format(date, getDateFormatToUse('time', profile), {
      locale: localeLanguage,
    });
    return `${relativeDateLocales[lang].yesterday} ${formattedTime}`;
  }

  if (isThisWeek(date, { weekStartsOn: 1 })) {
    if (formatType === 'date') {
      return format(date, 'EEEE', { locale: localeLanguage });
    }
    const formattedTime = format(date, getDateFormatToUse('time', profile), {
      locale: localeLanguage,
    });
    return `${format(date, 'EEEE', { locale: localeLanguage })} ${formattedTime}`;
  }

  // Apply format according to account profile
  return format(date, dateFormatToUse, { locale: localeLanguage });
};
